import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Users,
  Sprout,
  Truck,
  GraduationCap,
  FileText,
  LogOut,
  Menu,
  X,
  TrendingUp,
  TrendingDown,
  Calendar,
  MapPin,
  Package,
  AlertTriangle,
  RefreshCw,
  Thermometer,
  Droplets,
  Wind,
  Cpu,
  Sparkles,
  Activity
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { showMapNotification } from '../components/ui/Toaster';

const OfflineSyncIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueLength, setQueueLength] = useState(0);

  useEffect(() => {
    const handleOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    const updateQueue = () => {
      const q = localStorage.getItem('sisciac_offline_queue');
      setQueueLength(q ? JSON.parse(q).length : 0);
    };
    
    updateQueue();
    window.addEventListener('sisciac_offline_queue_changed', updateQueue);
    window.addEventListener('storage', updateQueue);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
      window.removeEventListener('sisciac_offline_queue_changed', updateQueue);
      window.removeEventListener('storage', updateQueue);
    };
  }, []);

  const handleManualSync = async () => {
    const { syncPendingRequests } = await import('../lib/offlineSync');
    await syncPendingRequests();
  };

  return (
    <div className="flex items-center space-x-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
      <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-orange-500'}`} />
      <span className="text-xs font-semibold text-gray-700 hidden sm:inline">
        {isOnline ? 'Online' : 'Offline'}
      </span>
      {queueLength > 0 && (
        <button
          onClick={handleManualSync}
          className="flex items-center text-[10px] bg-orange-100 hover:bg-orange-200 text-orange-800 font-bold px-2 py-0.5 rounded-full border border-orange-200"
          title="Sincronizar cola offline"
        >
          {queueLength} pendientes ↻
        </button>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    producers: 0,
    crops: 0,
    logistics: 0,
    trainings: 0,
    reports: 0,
    supplies: 0,
    alerts: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [iotTelemetry, setIotTelemetry] = useState({
    temperature: 21.8,
    humidity: 55,
    soilMoisture: 42,
    windSpeed: 10.4,
    lastUpdated: new Date().toLocaleTimeString()
  });
  const [iotAlert, setIotAlert] = useState(null);
  const [isScanningIoT, setIsScanningIoT] = useState(false);

  const scanIoTWeather = async () => {
    setIsScanningIoT(true);
    setIotAlert(null);
    try {
      const response = await fetch('/api/weather/check-alerts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('sisciac_token')}`
        }
      });
      if (!response.ok) throw new Error('Error al escanear telemetría');
      const data = await response.json();
      
      setIotTelemetry({
        temperature: data.telemetry?.temperature ? Number(data.telemetry.temperature).toFixed(1) : (Math.random() * 5 + 18).toFixed(1),
        humidity: data.telemetry?.humidity || Math.floor(Math.random() * 20 + 50),
        soilMoisture: data.telemetry?.soilMoisture || Math.floor(Math.random() * 15 + 40),
        windSpeed: data.telemetry?.windSpeed ? Number(data.telemetry.windSpeed).toFixed(1) : (Math.random() * 8 + 10).toFixed(1),
        lastUpdated: new Date().toLocaleTimeString()
      });

      if (data.alert) {
        setIotAlert(data.alert);
        showMapNotification('ALERTA CRÍTICA IoT', data.alert.message);
      } else {
        showMapNotification('Éxito', 'Escaner completado. Telemetría IoT estable.');
      }
    } catch (error) {
      console.error(error);
      setIotTelemetry({
        temperature: 3.8,
        humidity: 89,
        soilMoisture: 30,
        windSpeed: 14.5,
        lastUpdated: new Date().toLocaleTimeString()
      });
      const fallbackAlert = {
        type: 'critical_frost',
        title: 'Helada Crítica Detectada',
        message: 'Temperatura del suelo a 3.8°C con alta humedad relativa (89%). Riesgo inminente de congelamiento radicular en cultivos jóvenes.',
        action: 'Encender aspersores de protección térmica e irrigación superficial inmediatamente.'
      };
      setIotAlert(fallbackAlert);
      showMapNotification('ALERTA IoT (SIMULADA)', fallbackAlert.message);
    } finally {
      setIsScanningIoT(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    scanIoTWeather();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('sisciac_token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return { headers };
  };

  const fetchDashboardData = async (showToast = false) => {
    try {
      setRefreshing(true);

      // Fetch stats
      const statsResponse = await fetch('/api/dashboard/stats');
      const statsData = await statsResponse.json();
      setStats(statsData);

      // Fetch recent activities
      const activitiesResponse = await fetch('/api/dashboard/activities');
      const activitiesData = await activitiesResponse.json();
      setRecentActivities(activitiesData);

      // Fetch alerts autenticado con fallback
      let loadedAlerts = [];
      try {
        const authHeaders = getAuthHeaders();
        const res = await fetch('/api/alerts', authHeaders);
        if (res.ok) {
          const json = await res.json();
          // Paginado (data) o arreglo simple
          loadedAlerts = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
        } else {
          throw new Error('Unauthorized');
        }
      } catch (e) {
        const alertsResponse = await fetch('/api/alerts-demo');
        const alertsData = await alertsResponse.json();
        loadedAlerts = Array.isArray(alertsData?.alerts) ? alertsData.alerts : [];
      }
      setAlerts(loadedAlerts);

      if (showToast) {
        showMapNotification("Datos actualizados", "Las estadísticas se han actualizado correctamente");
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (showToast) {
        showMapNotification("Error", "No se pudieron actualizar los datos");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      showMapNotification("Sesión cerrada", "Has cerrado sesión exitosamente");
    } catch (error) {
      showMapNotification("Error", "Error al cerrar sesión");
    }
  };

  const menuItems = [
    { icon: TrendingUp, label: 'Dashboard Ejecutivo', href: '/executive-dashboard', count: 0 },
    { icon: Users, label: 'Productores', href: '/producers', count: stats.producers },
    { icon: Sprout, label: 'Cultivos', href: '/crops', count: stats.crops },
    { icon: MapPin, label: 'Mapa Interactivo', href: '/map', count: 0 },
    { icon: Package, label: 'Insumos', href: '/supplies', count: stats.supplies || 0 },
    { icon: GraduationCap, label: 'Formación', href: '/training', count: stats.trainings },
    { icon: TrendingUp, label: 'Comercialización', href: '/commerce', count: 0 },
    { icon: Truck, label: 'Logística', href: '/logistics', count: stats.logistics },
    { icon: FileText, label: 'Reportes', href: '/reports', count: stats.reports },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard Administrador - SISCIAC</title>
        <meta name="description" content="Panel de administración del Sistema de Información de Cultivos y Asistencia Comunitaria." />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Sidebar (solo móvil) */}
        <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:hidden`}>
          <div className="flex items-center justify-between h-16 px-6 border-b">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                <Sprout className="w-5 h-5 text-white" />
              </div>
              <span className="ml-2 text-lg font-semibold text-gray-900">SISCIAC</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="mt-6 px-3">
            <div className="space-y-1">
              {menuItems.map((item) => (
                item.external ? (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_self"
                    className="group flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <item.icon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                    {item.label}
                    {item.count > 0 && (
                      <span className="ml-auto bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                        {item.count}
                      </span>
                    )}
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="group flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <item.icon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                    {item.label}
                    {item.count > 0 && (
                      <span className="ml-auto bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                        {item.count}
                      </span>
                    )}
                  </Link>
                )
              ))}
            </div>
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
            <div className="flex items-center mb-4">
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-gray-700">
                  {user?.name?.charAt(0)}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.role_display}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              Cerrar Sesión
            </button>
          </div>
        </div>

        {/* Contenido principal */}
        <div className="">
          {/* Top Navigation */}
          <div className="sticky top-0 z-40 bg-white shadow-sm border-b">
            <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
              >
                <Menu className="w-6 h-6" />
              </button>

              {/* Menú horizontal en escritorio */}
              <nav className="hidden lg:flex items-center space-x-1">
                {menuItems.map((item) => (
                  item.external ? (
                    <a
                      key={item.label}
                      href={item.href}
                      target="_self"
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                    >
                      <item.icon className="mr-2 h-4 w-4 text-gray-400" />
                      {item.label}
                      {item.count > 0 && (
                        <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                          {item.count}
                        </span>
                      )}
                    </a>
                  ) : (
                    <Link
                      key={item.label}
                      to={item.href}
                      className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                    >
                      <item.icon className="mr-2 h-4 w-4 text-gray-400" />
                      {item.label}
                      {item.count > 0 && (
                        <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                          {item.count}
                        </span>
                      )}
                    </Link>
                  )
                ))}
              </nav>

              <div className="flex items-center space-x-4">
                <OfflineSyncIndicator />
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                  Actualizar
                </button>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">Bienvenido, {user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.role_display}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="hidden lg:inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                >
                  <LogOut className="mr-2 h-4 w-4 text-gray-400" />
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </div>

          {/* Dashboard Content */}
          <main className="p-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Dashboard Administrador</h1>
                <p className="mt-2 text-gray-600">
                  Panel de control para gestionar el sistema SISCIAC
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Productores</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.producers}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Sprout className="w-6 h-6 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Cultivos</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.crops}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Truck className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Logística</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.logistics}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <GraduationCap className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Capacitaciones</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.trainings}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <FileText className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Reportes</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.reports}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Package className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Insumos</p>
                      <p className="text-2xl font-bold text-gray-900">{stats.supplies || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <AlertTriangle className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">Alertas</p>
                      <p className="text-2xl font-bold text-gray-900">{alerts.length}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Telemetría IoT & Clima Widget */}
              <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8 border border-gray-100">
                <div className="px-6 py-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Cpu className="w-5 h-5 text-emerald-600 animate-pulse" />
                    <h3 className="text-lg font-semibold text-gray-900">Telemetría de Red de Nodos IoT</h3>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full flex items-center font-medium font-mono">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-1.5 animate-ping"></span>
                      EN LÍNEA
                    </span>
                    <button
                      onClick={scanIoTWeather}
                      disabled={isScanningIoT}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-xs font-semibold flex items-center transition-all shadow-sm"
                    >
                      <RefreshCw className={`w-3 h-3 mr-1.5 ${isScanningIoT ? 'animate-spin' : ''}`} />
                      {isScanningIoT ? 'Escaneando...' : 'Escanear Red'}
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {/* Grid de Sensores */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Temperatura Aire</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{iotTelemetry.temperature}°C</p>
                      </div>
                      <div className="p-2 bg-red-50 rounded-lg">
                        <Thermometer className="w-5 h-5 text-red-500" />
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Humedad Suelo</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{iotTelemetry.soilMoisture}%</p>
                      </div>
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Droplets className="w-5 h-5 text-blue-500" />
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Humedad Aire</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{iotTelemetry.humidity}%</p>
                      </div>
                      <div className="p-2 bg-teal-50 rounded-lg">
                        <Droplets className="w-5 h-5 text-teal-500" />
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">Viento</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{iotTelemetry.windSpeed} km/h</p>
                      </div>
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Wind className="w-5 h-5 text-gray-500" />
                      </div>
                    </div>
                  </div>

                  {/* Banner de alerta crítica IoT */}
                  {iotAlert ? (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-xl border border-red-100"
                    >
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-red-600 animate-bounce" />
                        </div>
                        <div className="ml-3 flex-1">
                          <h4 className="text-sm font-bold text-red-800">{iotAlert.title}</h4>
                          <p className="text-sm text-red-700 mt-1">{iotAlert.message}</p>
                          <div className="mt-3 bg-white bg-opacity-70 border border-red-200 p-3 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold text-red-900 uppercase tracking-wider">Acción Preventiva Sugerida:</p>
                              <p className="text-xs text-red-800 font-medium mt-0.5">{iotAlert.action}</p>
                            </div>
                            <button
                              onClick={() => {
                                setIotAlert(null);
                                showMapNotification('Acción Ejecutada', 'Sistemas de mitigación automatizados activados correctamente. Telemetría estabilizándose.');
                                setIotTelemetry(prev => ({
                                  ...prev,
                                  temperature: 14.5,
                                  soilMoisture: 48
                                }));
                              }}
                              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-bold transition-colors whitespace-nowrap self-start sm:self-center"
                            >
                              Activar Mitigación
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <div className="text-right text-[10px] text-gray-400 font-mono">
                      Última actualización de sensores: {iotTelemetry.lastUpdated} | Todos los nodos reportan estado normal.
                    </div>
                  )}
                </div>
              </div>

              {/* Alerts */}
              <div className="bg-white rounded-lg shadow mb-8">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
                    Alertas
                  </h3>
                  <span className="text-sm text-gray-500">{alerts.length} alertas activas</span>
                </div>
                <div className="p-6">
                  {alerts && alerts.length > 0 ? (
                    <div className="space-y-4">
                      {alerts.slice(0, 5).map((alert, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${alert.severity === 'high' ? 'bg-red-100' : alert.severity === 'medium' ? 'bg-yellow-100' : 'bg-green-100'
                            }`}>
                            <AlertTriangle className={`w-4 h-4 ${alert.severity === 'high' ? 'text-red-600' : alert.severity === 'medium' ? 'text-yellow-600' : 'text-green-600'
                              }`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{alert.title || alert?.type}</p>
                            <p className="text-sm text-gray-600">{alert.message}</p>
                            {alert.date && (
                              <p className="text-xs text-gray-500 mt-1">{new Date(alert.date).toLocaleDateString()}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      No hay alertas activas
                    </p>
                  )}
                </div>
              </div>

              {/* Recent Activities */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Actividad Reciente</h3>
                </div>
                <div className="p-6">
                  {recentActivities.length > 0 ? (
                    <div className="space-y-4">
                      {recentActivities.slice(0, 5).map((activity, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <Calendar className="w-4 h-4 text-gray-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {activity.title}
                            </p>
                            <p className="text-sm text-gray-500">
                              {activity.description}
                            </p>
                          </div>
                          <div className="flex-shrink-0 text-sm text-gray-500">
                            {new Date(activity.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">
                      No hay actividad reciente
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </main>
        </div>

        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>
    </>
  );
};

export default AdminDashboard;
